import { useEffect, useRef, useState } from "react";
import "./Landing.css";

/**
 * ReceiptRaptor marketing landing page.
 *
 * Wire-up:
 *  - Mount as the public "/" route for signed-out visitors; redirect
 *    authenticated users to the dashboard.
 *  - CTAs point at "/signup". Swap the plain <a href="/signup"> for your
 *    router's <Link to="/signup"> if you want client-side navigation.
 *  - Fonts are self-hosted @font-face rules in Landing.css (SIL OFL); the
 *    woff2 files live in src/assets/fonts and are bundled by Vite.
 */
export default function Landing() {
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // scroll-reveal
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const els = root.querySelectorAll<HTMLElement>(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add("in");
            io.unobserve(en.target);
          }
        });
      },
      { threshold: 0.14 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // optional: set the document title for this route
  useEffect(() => {
    const prev = document.title;
    document.title =
      "ReceiptRaptor — Receipts, signatures & customers in one sharp system";
    return () => {
      document.title = prev;
    };
  }, []);

  const closeMenuOnLink = (e: React.MouseEvent<HTMLElement>) => {
    if ((e.target as HTMLElement).tagName === "A") setMenuOpen(false);
  };

  const Logo = () => (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" aria-hidden="true">
      <path d="M4 5h13l5 5-9 11L4 16V5z" stroke="#E2A233" strokeWidth="2" strokeLinejoin="round" />
      <path d="M9 10.5l3.5 3.5M16 9l-4 9" stroke="#E2A233" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );

  const Check = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
      <path d="M5 13l4 4 10-11" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  return (
    <div className="rr-landing" ref={rootRef} id="top">
      {/* NAV */}
      <header className="nav">
        <div className="wrap nav__in">
          <a className="brand" href="#top" aria-label="ReceiptRaptor home">
            <Logo />
            ReceiptRaptor
          </a>
          <nav
            className={"nav__links" + (menuOpen ? " open" : "")}
            onClick={closeMenuOnLink}
          >
            <a href="#features">Features</a>
            <a href="#how">How it works</a>
            <a href="#pricing">Pricing</a>
            <a className="btn btn--amber" href="/signup">Get started</a>
          </nav>
          <button
            className="nav__toggle"
            aria-label="Open menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
          >
            <span></span><span></span><span></span>
          </button>
        </div>
      </header>

      <main>
        {/* HERO */}
        <section className="hero">
          <div className="hero__glow"></div>
          <div className="wrap hero__grid">
            <div>
              <p className="eyebrow">For independent operators</p>
              <h1>Get paid, get it&nbsp;<em>signed</em>, get on with your day.</h1>
              <p className="hero__sub">
                ReceiptRaptor is the configurable system independent operators use to
                generate receipts, collect signatures, and keep every customer in one
                place. You define the services — it handles the paperwork.
              </p>
              <div className="hero__cta">
                <a className="btn btn--amber" href="/signup">Get started</a>
                <a className="btn btn--ghost" href="#how">See how it works</a>
              </div>
              <p className="hero__trust">
                Sign up in minutes&nbsp; · &nbsp;<b>$19/month</b>&nbsp; · &nbsp;Cancel anytime
              </p>
            </div>

            <div className="receipt-stage">
              <div
                className="receipt"
                role="img"
                aria-label="A sample ReceiptRaptor receipt, signed and marked paid."
              >
                <div className="r-head">
                  <span className="r-brand">ReceiptRaptor</span>
                  <span className="r-no">NO. 00428</span>
                </div>
                <div className="r-meta"><span>OAK &amp; PINE STUDIO</span><span>2026-06-28</span></div>
                <div className="r-line"><span>Service call</span><span>$120.00</span></div>
                <div className="r-line"><span>Materials</span><span>$48.50</span></div>
                <div className="r-line"><span>Follow-up visit</span><span>$60.00</span></div>
                <div className="r-rule"></div>
                <div className="r-total"><span>TOTAL</span><span>$228.50</span></div>
                <div className="r-sign">
                  <div className="r-sign__label">Authorized signature</div>
                  <div className="r-sign__line">
                    <svg viewBox="0 0 150 30" aria-hidden="true">
                      <path d="M4 22 C 14 6, 22 6, 26 18 S 40 30, 48 14 C 54 4, 60 8, 64 18 C 70 30, 82 8, 96 14 C 108 19, 118 10, 146 12" />
                    </svg>
                  </div>
                </div>
                <div className="stamp">PAID</div>
              </div>
            </div>
          </div>
        </section>

        {/* MARQUEE */}
        <div className="marquee" aria-hidden="true">
          <div className="marquee__row">
            {[
              "Lawn care","Tutoring","Auto detailing","Consulting","Home repair","Personal training",
              "Grooming","House cleaning","Photography","Bookkeeping","Pool service","Massage therapy",
              "Lawn care","Tutoring","Auto detailing","Consulting","Home repair","Personal training",
              "Grooming","House cleaning","Photography","Bookkeeping","Pool service","Massage therapy",
            ].map((s, i) => (
              <span className="chip" key={i}><b>+</b>{s}</span>
            ))}
          </div>
        </div>

        {/* FEATURES */}
        <section className="section" id="features">
          <div className="wrap">
            <div className="section__head reveal">
              <p className="eyebrow">What you get</p>
              <h2>One place for the work after the work.</h2>
              <p>
                Configure it once around how you actually operate. Then every receipt,
                signature, and customer record lives in the same sharp system.
              </p>
            </div>
            <div className="features">
              <div className="card reveal">
                <div className="card__ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 7h16M4 12h16M4 17h10" strokeLinecap="round" /></svg></div>
                <h3>Define your own services</h3>
                <p>Set up the services you offer and the items to track under each. The platform shapes itself to your business — not the other way around.</p>
              </div>
              <div className="card reveal">
                <div className="card__ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 3h9l3 3v15H6z" strokeLinejoin="round" /><path d="M9 11h6M9 15h6" strokeLinecap="round" /></svg></div>
                <h3>Generate clean receipts</h3>
                <p>Turn a finished job into a branded receipt in seconds — itemized, totaled, and ready to send. No spreadsheets, no carbon copies.</p>
              </div>
              <div className="card reveal">
                <div className="card__ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 18c4-6 8 4 12-2s5-6 6-7" strokeLinecap="round" /><path d="M3 21h18" strokeLinecap="round" /></svg></div>
                <h3>Collect signatures that stick</h3>
                <p>Send a document, get it signed electronically, and keep a tamper-evident record — designed to meet ESIGN &amp; UETA.</p>
              </div>
              <div className="card reveal">
                <div className="card__ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M4 9h16M9 4v16" strokeLinecap="round" /></svg></div>
                <h3>Reusable document templates</h3>
                <p>Build the agreements and forms you send over and over, fill them with customer details automatically, and reuse them every time.</p>
              </div>
              <div className="card reveal">
                <div className="card__ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="8" r="3.2" /><path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5M16 5l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" /></svg></div>
                <h3>Every customer in one place</h3>
                <p>Keep contacts, history, and balances together, so you always know who you served, what for, and what's still owed.</p>
              </div>
              <div className="card reveal">
                <div className="card__ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3v9l6 3" strokeLinecap="round" strokeLinejoin="round" /><circle cx="12" cy="12" r="9" /></svg></div>
                <h3>Track what you delivered</h3>
                <p>Log visits and service delivery as they happen, so your records match reality when it's time to bill or settle a question.</p>
              </div>
            </div>
          </div>
        </section>

        {/* HOW */}
        <section className="section how" id="how">
          <div className="wrap">
            <div className="section__head reveal">
              <p className="eyebrow">How it works</p>
              <h2>Three steps. Then it gets out of your way.</h2>
            </div>
            <div className="steps">
              <div className="step reveal">
                <div className="step__n">01 — CONFIGURE</div>
                <div className="step__bar"></div>
                <h3>Set up your services</h3>
                <p>Name the services you offer and the items you track under each. A few minutes of setup, and the system fits your business.</p>
              </div>
              <div className="step reveal">
                <div className="step__n">02 — SERVE</div>
                <div className="step__bar"></div>
                <h3>Do the work, log it</h3>
                <p>Add customers, record what you delivered, and keep balances current as you go. Everything stays in one running record.</p>
              </div>
              <div className="step reveal">
                <div className="step__n">03 — CLOSE OUT</div>
                <div className="step__bar"></div>
                <h3>Bill it, sign it, done</h3>
                <p>Generate the receipt, track document delivery and expirations, collect signatures. Your customers sign from a link — no account, no friction.</p>
              </div>
            </div>
          </div>
        </section>

        {/* PRICING */}
        <section className="section" id="pricing">
          <div className="wrap">
            <div className="section__head reveal" style={{ marginLeft: "auto", marginRight: "auto", textAlign: "center" }}>
              <p className="eyebrow">Pricing</p>
              <h2>One plan. No surprises.</h2>
              <p>Straightforward pricing for independent operators. Sign up and be up and running in minutes.</p>
            </div>
            <div className="price-wrap reveal">
              <div className="price">
                <span className="price__tag">ReceiptRaptor — Standard</span>
                <div className="price__amt"><b>$19</b><span>/ month</span></div>
                <p className="price__note">Billed monthly · card up front · cancel anytime</p>
                <ul>
                  <li><Check />Configurable services &amp; tracked items</li>
                  <li><Check />Branded receipts &amp; reusable templates</li>
                  <li><Check />Electronic signatures (ESIGN / UETA)</li>
                  <li><Check />Client management &amp; service tracking</li>
                  <li><Check />Link-based signing — no account for your customers</li>
                </ul>
                <a className="btn btn--amber" href="/signup">Get started</a>
                <p className="price__fine">Fair-use limits apply. See the <a href="/legal/ReceiptRaptor-Acceptable-Use-Policy.md">Acceptable Use Policy</a>.</p>
              </div>
            </div>
          </div>
        </section>

        {/* START */}
        <section className="cta-band" id="start">
          <div className="wrap">
            <div className="cta-card reveal">
              <p className="eyebrow">Get started</p>
              <h2>Create your account in minutes.</h2>
              <p>All it takes is an email and a card. Set up your services, send your first receipt, and collect a signature today — no waitlist, no sales call.</p>
              <a className="btn btn--amber btn--lg" href="/signup">Start now — $19/month</a>
              <p className="cta-card__fine">Card up front · cancel anytime · no long-term contract</p>
            </div>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="foot">
        <div className="wrap">
          <div className="foot__in">
            <span className="brand" style={{ fontSize: "17px" }}>
              <Logo />
              ReceiptRaptor
            </span>
            <nav className="foot__links">
              <a href="#features">Features</a>
              <a href="#pricing">Pricing</a>
              <a href="/legal/ReceiptRaptor-Terms-of-Service.md">Terms</a>
              <a href="/legal/ReceiptRaptor-Privacy-Policy.md">Privacy</a>
              <a href="mailto:hello@receiptraptor.com">Contact</a>
            </nav>
          </div>
          <p className="foot__fine">© {new Date().getFullYear()} ReceiptRaptor LLC · Independently built in Oro Valley, Arizona.</p>
        </div>
      </footer>
    </div>
  );
}
